<div class="d-flex flex-column h-100 dialog-wrapper" tabindex="-1" cdkFocusInitial>
  <div mat-dialog-title>
    <button mat-flat-button (click)="onCancel()" class="button--square mr-3">
      <mat-icon svgIcon="arrow-left"></mat-icon>
    </button>
    <div class="d-flex flex-column">
      <div class="h3 mb-0">{{title}}</div>
    </div>
  </div>

  <mat-dialog-content>
    <h4 class="pb-2 mb-3 border-bottom" i18n="@@dpr.dprActivitiesDialogMode.txtActivityDetails">Activity details</h4>
    <form [formGroup]="form">
      <!-- Activity Group, Activity, Location Selects -->
      <ng-container *ngFor="let ctrl of selectControls">
        <app-searchable-select-form-control
          [formControlName]="ctrl.formControlName"
          [label]="ctrl.label"
          [shouldDisplayLabel]="true"
          [options]="this[ctrl.optionsKey] | sort : 'priority' : 'asc'"
          [hasInitialFocusDisabled]="true"
        ></app-searchable-select-form-control>
      </ng-container>

      <!-- Time Pickers -->
      <div class="d-flex pb-3">
        <div class="pr-2">
          <app-time
            label="Start"
            [ngClass]="{'time-disabled': !data.canEdit}"
            [time]="startTime"
            [isDisabled]="!data.canEdit"
            [isRequired]="true"
            (timeChanged)="onStartTimeChanged($event)"
          ></app-time>
        </div>
        <div class="pl-2">
          <app-time
            label="Finish"
            [ngClass]="{'time-disabled': !data.canEdit}"
            [time]="finishTime"
            [isDisabled]="isActivityMilestone || !data.canEdit"
            [isRequired]="!data.enableFinishTimeAutoPopulationForActivities"
            (timeChanged)="onFinishTimeChanged($event)"
          ></app-time>
        </div>
      </div>

      <!-- Comment Field -->
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>{{StaticMessage.CommentLabel | staticMessage}}</mat-label>
        <textarea
          matInput
          [maxlength]="Constants.FieldLimits.maxGridCommentLength"
          cdkTextareaAutosize
          cdkAutosizeMinRows="0"
          cdkAutosizeMaxRows="2"
          formControlName="comment"
        ></textarea>
      </mat-form-field>

      <!-- Custom Data Fields -->
      <div formArrayName="customDataFormArray">
        <ng-container *ngIf="customDataFormArray?.length > 0 && activityId">
          <h4 class="pb-2 mb-3 border-bottom">{{StaticMessage.CustomDataLabel | staticMessage}}</h4>
        </ng-container>
        <!-- Hidden is needed to preserve previous CD values when transitioning between activity groups where selected activity is still null -->
        <div [hidden]="!activityId">
          <ng-container *ngFor="let customDataControl of customDataFormArray.controls; let i = index">
            <ng-container [formGroupName]="i">
              <ng-container [ngSwitch]="customDataControl.get('dataType')?.value">
                <!-- FreeText -->
                <mat-form-field *ngSwitchCase="DataType.FreeText" appearance="fill">
                  <mat-label>{{ customDataControl.get('name')?.value }}</mat-label>
                  <input
                    matInput
                    [maxlength]="Constants.FieldLimits.maxFreeTextDataTypeLimit"
                    formControlName="value"
                    [required]="customDataControl.get('isMandatory')?.value"
                  />
                </mat-form-field>

                <!-- Number -->
                <mat-form-field *ngSwitchCase="DataType.Number" appearance="fill">
                  <mat-label>{{ customDataControl.get('name')?.value }}</mat-label>
                  <mat-icon matPrefix svgIcon="sort-numeric"></mat-icon>
                  <input
                    matInput
                    appNumericInput
                    formControlName="value"
                    [required]="customDataControl.get('isMandatory')?.value"
                    inputmode="decimal"
                    autocomplete="off"
                    class="text-right"
                    (input)="onInputChange($event, i)"
                    [options]="customDataControl.get('numericInputConfig')?.value"
                  />
                </mat-form-field>

                <!-- Percentage -->
                <mat-form-field *ngSwitchCase="DataType.Percentage" appearance="fill">
                  <mat-label>{{ customDataControl.get('name')?.value }}</mat-label>
                  <mat-icon matPrefix svgIcon="sort-numeric"></mat-icon>
                  <input
                    matInput
                    appNumericInput
                    formControlName="value"
                    [required]="customDataControl.get('isMandatory')?.value"
                    inputmode="decimal"
                    autocomplete="off"
                    class="text-right"
                    (input)="onInputChange($event, i)"
                    [options]="customDataControl.get('numericInputConfig')?.value"
                  />
                  <span matTextSuffix>%</span>
                </mat-form-field>

                <!-- List -->
                <app-searchable-select-form-control
                  *ngSwitchCase="DataType.List"
                  appearance="fill"
                  formControlName="value"
                  [label]="customDataControl.get('name')?.value"
                  [shouldDisplayLabel]="true"
                  [isRequired]="customDataControl.get('isMandatory')?.value"
                  [options]="customDataControl.get('selectOptions')?.value"
                  [shouldIncludeNoneOption]="true"
                  [hasInitialFocusDisabled]="true"
                ></app-searchable-select-form-control>

                <!-- Boolean -->
                <app-searchable-select-form-control
                  *ngSwitchCase="DataType.Boolean"
                  appearance="fill"
                  formControlName="value"
                  [label]="customDataControl.get('name')?.value"
                  [shouldDisplayLabel]="true"
                  [isRequired]="customDataControl.get('isMandatory')?.value"
                  [options]="customDataControl.get('selectOptions')?.value"
                  [shouldIncludeNoneOption]="true"
                  [hasInitialFocusDisabled]="true"
                ></app-searchable-select-form-control>

                <!-- Date -->
                <mat-form-field *ngSwitchCase="DataType.Date" appearance="fill">
                  <mat-label>{{ customDataControl.get('name')?.value }}</mat-label>
                  <input
                    matInput
                    [matDatepicker]="datePicker"
                    formControlName="value"
                    [required]="customDataControl.get('isMandatory')?.value"
                    [min]="minDate"
                    readonly
                    (click)="datePicker.open()"
                  />
                  <mat-datepicker-toggle matSuffix [for]="datePicker"></mat-datepicker-toggle>
                  <mat-datepicker touchUi #datePicker>
                    <mat-datepicker-actions>
                      <button
                        data-testId="save-date-time-selection"
                        mat-flat-button
                        color="secondary"
                        class="button--small"
                        matDatepickerApply
                        (click)="saveDateTime(datePicker, i)"
                      >
                        <mat-icon svgIcon="check"></mat-icon>
                        Save
                      </button>
                      <button
                        data-testId="cancel-date-time-selection"
                        mat-button
                        color="primary"
                        class="button--small"
                        matDatepickerCancel
                      >
                        Cancel
                      </button>
                      <button
                        *ngIf="selectedDateTimes.get(datePicker)?.date"
                        data-testId="clear-calendar"
                        mat-button
                        color="primary"
                        class="button--small ml-auto"
                        (click)="clearDateTime(datePicker)"
                        matDatepickerCancel
                      >
                        Clear
                      </button>
                    </mat-datepicker-actions>
                  </mat-datepicker>
                </mat-form-field>

                <!-- Datetime -->
                <mat-form-field *ngSwitchCase="DataType.Datetime" appearance="fill">
                  <mat-label>{{ customDataControl.get('name')?.value }}</mat-label>
                  <input
                    matInput
                    formControlName="value"
                    [required]="customDataControl.get('isMandatory')?.value"
                    readonly
                    [value]="selectedDateTimes.get(datetimePicker)?.formattedValue"
                    (click)="$event.preventDefault(); openDateTimeDialog(datetimePicker)"
                  />

                  <input
                    class="d-none"
                    formControlName="value"
                    [matDatepicker]="datetimePicker"
                    [min]="minDate"
                    readonly
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    [for]="datetimePicker"
                    (click)="$event.preventDefault(); openDateTimeDialog(datetimePicker)"
                  ></mat-datepicker-toggle>
                  <mat-datepicker touchUi #datetimePicker>
                    <mat-datepicker-actions>
                      <div #timePicker class="d-flex w-100 border-top pt-1">
                        <app-time
                          [time]="pendingTimes.get(datetimePicker)"
                          (timeChanged)="onTimeChanged(datetimePicker, $event)"
                        ></app-time>
                      </div>
                      <div class="buttonsWrapper pt-2">
                        <button
                          data-testId="save-date-time-selection"
                          mat-flat-button
                          color="secondary"
                          class="button--small"
                          matDatepickerApply
                          (click)="saveDateTime(datetimePicker, i)"
                        >
                          <mat-icon svgIcon="check"></mat-icon>
                          Save
                        </button>
                        <button
                          data-testId="cancel-date-time-selection"
                          mat-button
                          color="primary"
                          class="button--small"
                          matDatepickerCancel
                        >
                          Cancel
                        </button>
                        <button
                          *ngIf="selectedDateTimes.get(datetimePicker)?.date"
                          data-testId="clear-calendar"
                          mat-button
                          color="primary"
                          class="button--small ml-auto"
                          (click)="clearDateTime(datetimePicker)"
                          matDatepickerCancel
                        >
                          Clear
                        </button>
                      </div>
                    </mat-datepicker-actions>
                  </mat-datepicker>
                </mat-form-field>
              </ng-container>
            </ng-container>
          </ng-container>
        </div>
      </div>
    </form>
  </mat-dialog-content>

  <mat-dialog-actions class="buttonsWrapper fixed-bottom">
    <ng-container *ngIf="data.canEdit; else readOnlyAction">
      <button mat-flat-button color="primary" (click)="onDone()" [disabled]="!this.isFormValid">
        <mat-icon svgIcon="check"></mat-icon>
        <span>{{StaticMessage.DoneButton | staticMessage}}</span>
      </button>
      <button mat-button color="primary" (click)="onCancel()">{{StaticMessage.CancelButton | staticMessage}}</button>
    </ng-container>
    <ng-template #readOnlyAction>
      <button mat-flat-button color="primary" (click)="onCancel()" cdkFocusInitial>
        {{StaticMessage.CloseButton | staticMessage}}
      </button>
    </ng-template>
  </mat-dialog-actions>
</div>
